import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Send, MessageSquare } from "lucide-react";
import { toast } from "sonner";

export default function MessageThread({ currentUserId }) {
  const [data, setData] = useState({ counterpart: null, messages: [] });
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/messages");
      setData(data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, []);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [data.messages.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    try {
      await api.post("/messages", { body: body.trim() });
      setBody("");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't send message");
    } finally { setBusy(false); }
  };

  return (
    <div className="card-flat flex flex-col h-[460px]" data-testid="message-thread">
      <div className="flex items-center justify-between p-4 border-b border-brand-border">
        <div className="flex items-center gap-2">
          <MessageSquare size={18} className="text-brand-cyan" />
          <div>
            <div className="eyebrow">Direct Messages</div>
            <div className="text-sm font-semibold text-brand-navy mt-0.5">
              {data.counterpart ? `${data.counterpart.name} · ${data.counterpart.role}` : "Waiting for counterpart…"}
            </div>
          </div>
        </div>
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-brand-ice">
        {data.messages.length === 0 ? (
          <div className="text-center text-sm text-brand-slate/60 mt-12">No messages yet — start the conversation.</div>
        ) : data.messages.map((m) => {
          const mine = m.from_id === currentUserId;
          return (
            <div key={m.id} data-testid={`msg-${m.id}`} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[78%] px-3 py-2 text-sm ${mine ? "bg-brand-cyan text-white" : "bg-white border border-brand-border text-brand-slate"}`}>
                {!mine && <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-brand-cyan mb-1">{m.from_name} · {m.from_role}</div>}
                <div className="whitespace-pre-wrap break-words leading-relaxed">{m.body}</div>
                <div className={`text-[10px] mt-1 ${mine ? "text-white/70" : "text-brand-slate/50"}`}>{new Date(m.created_at).toLocaleString()}</div>
              </div>
            </div>
          );
        })}
      </div>
      <form onSubmit={send} className="p-3 border-t border-brand-border flex gap-2" data-testid="message-form">
        <input
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={data.counterpart ? `Message ${data.counterpart.name}…` : "Type a message…"}
          className="flex-1 border border-brand-border px-3 py-2 text-sm focus:outline-none focus:border-brand-cyan"
          data-testid="message-input"
        />
        <button disabled={busy || !body.trim()} className="btn-cyan !py-2 !px-4 disabled:opacity-50" data-testid="message-send">
          <Send size={14} /> {busy ? "…" : "Send"}
        </button>
      </form>
    </div>
  );
}
